import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { catchError, filter, forkJoin, of, pipe, switchMap, tap } from 'rxjs';
import {
  DateTimeService,
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { DateTime } from 'luxon';
import { getDateTime } from '@shared/utils';
import {
  EdgeConnectionGrid,
  EdgeConnectionGridMetric,
  EdgeConnectionHourlyLatency,
  EdgeConnectionReportDay,
  EdgeConnectionReportQuery,
  EdgeConnectionReportState,
  EdgeConnectionRttSample,
  initialEdgeConnectionReportState,
} from '../entities/edge-connection-report.model';
import { AccessPointEdgeService } from '../infra/access-point-edge.service';

const OPS = ['load'] as const;

const HOURS_IN_DAY = 24;

/** Degraus da rampa de cor das grades. */
const GRID_LEVELS = 5;

/** Grade vazia — o que as computed devolvem enquanto não há relatório. */
const EMPTY_GRID: EdgeConnectionGrid = {
  rows: [],
  hourTotals: Array.from({ length: HOURS_IN_DAY }, () => 0),
  total: 0,
  max: 0,
};

/**
 * Intensidade da célula na rampa de cor.
 *
 * A escala é relativa ao maior valor da PRÓPRIA grade — degradações e passagens
 * têm ordens de grandeza diferentes e cada uma tem de usar a rampa toda. Os
 * quatro primeiros degraus são quartos do máximo; o quinto fica reservado para o
 * próprio pico, para a hora mais crítica não se diluir no degrau de cima.
 */
const cellLevel = (value: number, max: number): number => {
  if (value <= 0 || max <= 0) {
    return 0;
  }
  if (value >= max) {
    return GRID_LEVELS;
  }
  return Math.ceil((value / max) * (GRID_LEVELS - 1));
};

/** Monta a grade dia×hora de uma métrica, com a linha e a coluna de totais. */
const buildGrid = (
  days: EdgeConnectionReportDay[],
  metric: EdgeConnectionGridMetric,
): EdgeConnectionGrid => {
  if (!days.length) {
    return EMPTY_GRID;
  }

  const max = days.reduce(
    (highest, day) => day[metric].reduce((acc, value) => Math.max(acc, value ?? 0), highest),
    0,
  );

  const hourTotals = Array.from({ length: HOURS_IN_DAY }, (_, hour) =>
    days.reduce((sum, day) => sum + (day[metric][hour] ?? 0), 0),
  );

  const lastIndex = days.length - 1;

  return {
    rows: days.map((day, index) => ({
      date: day.date,
      weekday: day.weekday,
      isWeekend: day.weekday === 0 || day.weekday === 6,
      isPartial: index === 0 || index === lastIndex,
      isAggregated: day.aggregated,
      cells: Array.from({ length: HOURS_IN_DAY }, (_, hour) => {
        const value = day[metric][hour] ?? 0;
        return {
          hour,
          value,
          level: cellLevel(value, max),
          hasDrop: (day.drops[hour] ?? 0) > 0,
        };
      }),
      total: day[metric].reduce((sum, value) => sum + (value ?? 0), 0),
    })),
    hourTotals,
    total: hourTotals.reduce((sum, value) => sum + value, 0),
    max,
  };
};

/**
 * Pior RTT de cada hora do dia, somando todos os dias da janela.
 *
 * `t` chega em epoch UTC e `getDateTime` devolve-o no fuso da conta — o mesmo
 * que foi pedido ao servidor —, portanto a hora extraída aqui é a mesma hora
 * local que indexa as grades. Ler o epoch em UTC deslocaria a curva inteira
 * pelo offset da conta e poria o pico da noite de pé na madrugada.
 */
/** Mediana por interpolação linear entre as ordens vizinhas (R-7, o do Excel). */
const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) / 2;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return lower === upper
    ? sorted[lower]
    : sorted[lower] + (position - lower) * (sorted[upper] - sorted[lower]);
};

/** Uma hora de um dia concreto, já com o nível resolvido. */
type HourCell = { hour: number; typicalMs: number; peakMs: number; measured: number };

/**
 * Percorre a janela hora a hora, arrastando o último nível medido.
 *
 * `rttSamples` é um fluxo de MUDANÇAS de qualidade, não de medições periódicas.
 * Hora sem amostra é hora em que nada mudou — o nível anterior valeu nela — e
 * não hora sem dado. Reconstruir esse arrasto é o que dá as 24 horas
 * preenchidas sem inventar nenhum valor: cada uma ou foi medida, ou herdou o
 * que estava em vigor.
 *
 * O cursor e o nível corrente são variáveis locais de uma varredura única —
 * nada fora desta função é mutado, e a alternativa funcional custaria uma
 * releitura das amostras por hora da janela.
 */
/** Cada hora da janela, em ordem cronológica, já com os limites em epoch. */
const hourWindowsOf = (
  days: EdgeConnectionReportDay[],
): { hour: number; from: number; to: number }[] =>
  days.flatMap((day) => {
    const dayStart = getDateTime(day.date)?.startOf('day');
    if (!dayStart?.isValid) {
      return [];
    }

    return Array.from({ length: HOURS_IN_DAY }, (_, hour) => ({
      hour,
      from: dayStart.plus({ hours: hour }).toMillis(),
      to: dayStart.plus({ hours: hour + 1 }).toMillis(),
    }));
  });

/** Amostras válidas em ordem cronológica, que é o que a varredura consome. */
const timelineOf = (samples: EdgeConnectionRttSample[]): { millis: number; rttMs: number }[] =>
  samples
    .map((sample) => ({ at: getDateTime(sample.t), rttMs: sample.rttMs }))
    .filter((entry) => entry.at?.isValid && entry.rttMs > 0)
    .map((entry) => ({ millis: (entry.at as DateTime).toMillis(), rttMs: entry.rttMs }))
    .sort((a, b) => a.millis - b.millis);

const buildHourCells = (
  days: EdgeConnectionReportDay[],
  samples: EdgeConnectionRttSample[],
): HourCell[] => {
  const timeline = timelineOf(samples);
  const cells: HourCell[] = [];
  let cursor = 0;
  let carriedLevel: number | null = null;

  for (const window of hourWindowsOf(days)) {
    const inHour: number[] = [];

    while (cursor < timeline.length && timeline[cursor].millis < window.to) {
      carriedLevel = timeline[cursor].rttMs;
      if (timeline[cursor].millis >= window.from) {
        inHour.push(timeline[cursor].rttMs);
      }
      cursor++;
    }

    // Antes da primeira medição não há nível para arrastar: a hora fica de
    // fora em vez de nascer num valor arbitrário.
    if (inHour.length || carriedLevel !== null) {
      cells.push({
        hour: window.hour,
        typicalMs: inHour.length ? median(inHour) : (carriedLevel as number),
        peakMs: Math.max(...inHour, carriedLevel ?? 0),
        measured: inHour.length,
      });
    }
  }

  return cells;
};

const buildHourlyLatency = (
  days: EdgeConnectionReportDay[],
  samples: EdgeConnectionRttSample[],
): EdgeConnectionHourlyLatency[] => {
  const cells = buildHourCells(days, samples);

  return Array.from({ length: HOURS_IN_DAY }, (_, hour) => {
    const ofThisHour = cells.filter((cell) => cell.hour === hour);

    if (!ofThisHour.length) {
      return { hour, typicalMs: null, peakMs: null, samples: 0, carriedHours: 0 };
    }

    return {
      hour,
      typicalMs: median(ofThisHour.map((cell) => cell.typicalMs)),
      peakMs: Math.max(...ofThisHour.map((cell) => cell.peakMs)),
      samples: ofThisHour.reduce((sum, cell) => sum + cell.measured, 0),
      carriedHours: ofThisHour.filter((cell) => cell.measured === 0).length,
    };
  });
};

/**
 * Relatório de conexão de UM edge — o diagnóstico de rede da tela de pontos de
 * acesso. Estado próprio, e não mais um campo do `AccessPointEdgeStore`: aquele
 * vive à volta do hub em tempo real e do provisionamento, este carrega uma
 * fotografia por `edgeId` e é descartado ao sair da tela.
 *
 * O fuso vai no pedido e vem do cadastro do inquilino
 * (`DateTimeService.getUserZone()`), nunca do browser: é no fuso do
 * estabelecimento que "hora de pico" quer dizer alguma coisa.
 *
 * Falha de leitura fica só no `loadError` — a tela mostra o estado de erro no
 * lugar do relatório. Sem toast: o utilizador já está a olhar para a tela que
 * falhou, e o `ErrorStore` é para o que acontece fora do campo de visão.
 */
export const EdgeConnectionReportStore = signalStore(
  { providedIn: 'root' },
  withState<EdgeConnectionReportState>(initialEdgeConnectionReportState),
  withReqState(...OPS),
  withComputed((store) => ({
    /**
     * Edge sem eventos responde `200` com `days: []`: é estado vazio, e a tela
     * distingue-o de "ainda a carregar" e de "falhou".
     */
    hasReportData: computed<boolean>(() => (store.report()?.days.length ?? 0) > 0),

    /**
     * O directory não respondeu e os dias antigos ficaram de fora. Dado parcial
     * ANUNCIADO pelo servidor — calar isto seria apresentar meia janela como se
     * fosse o período pedido.
     */
    isHistoryUnavailable: computed<boolean>(
      () => store.report()?.period?.historyUnavailable === true,
    ),

    /**
     * Há dias servidos pelo rollup horário, que não têm `rttSamples`. Enquanto
     * isto for verdade, os dois gráficos de latência cobrem só a janela viva —
     * e a tela tem de o dizer, senão parece que o período inteiro foi medido.
     */
    hasAggregatedDays: computed<boolean>(
      () => store.report()?.days.some((day) => day.aggregated) === true,
    ),

    degradationsGrid: computed<EdgeConnectionGrid>(() =>
      buildGrid(store.report()?.days ?? [], 'degradations'),
    ),

    /**
     * `poor` é o subconjunto grave das degradações — as transições para `Poor`,
     * não para `Fair`. Mesma geometria das outras grades, de propósito: é a
     * comparação coluna a coluna que mostra se a hora ruim é ruim de verdade ou
     * só instável.
     */
    poorGrid: computed<EdgeConnectionGrid>(() =>
      buildGrid(store.report()?.days ?? [], 'poor'),
    ),

    passagesGrid: computed<EdgeConnectionGrid>(() =>
      buildGrid(store.report()?.days ?? [], 'passages'),
    ),

    /**
     * Distribuição de latência por hora do dia — a segunda camada da leitura:
     * o gráfico da linha do tempo mostra QUANDO aconteceu, este mostra em que
     * hora do dia costuma acontecer, e com que dispersão.
     */
    hourlyLatency: computed<EdgeConnectionHourlyLatency[]>(() =>
      buildHourlyLatency(store.report()?.days ?? [], store.report()?.rttSamples ?? []),
    ),

    /**
     * A taxa por hora cresce de faixa em faixa de lotação — é o veredito de que
     * a degradação acompanha a quantidade de gente na rede. A tela destaca a
     * última faixa só quando isto se verifica.
     */
    isOccupancyRateRising: computed<boolean>(() => {
      const bands = store.report()?.summary.occupancyBands ?? [];
      return (
        bands.length > 1 &&
        bands.every((band, index) => index === 0 || band.ratePerHour > bands[index - 1].ratePerHour)
      );
    }),
  })),
  withMethods(
    (
      store,
      service = inject(AccessPointEdgeService),
      dateTimeService = inject(DateTimeService),
    ) => ({
      load: rxMethod<EdgeConnectionReportQuery>(
        pipe(
          filter((query) => !!query?.edgeId),
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(({ edgeId, period }) =>
            forkJoin({
              report: service.getConnectionReport(
                edgeId,
                dateTimeService.getUserZone(),
                period,
              ),
              // O nome é decoração do cabeçalho e vem de outro serviço. Se o
              // directory falhar, a tela mostra o diagnóstico com o
              // identificador técnico — derrubar o relatório inteiro por causa
              // de um rótulo seria trocar o essencial pelo acessório.
              accessPoints: service
                .getAccessPointByEdgeId(edgeId)
                .pipe(catchError(() => of([]))),
            }).pipe(
              tapResponse({
                next: ({ report, accessPoints }) =>
                  patchState(
                    store,
                    { report, accessPointName: accessPoints[0]?.Name ?? null },
                    setPropLoaded('load'),
                  ),
                error: (e: HttpErrorResponse) => patchState(store, setPropError('load', e)),
              }),
            ),
          ),
        ),
      ),

      reset(): void {
        patchState(store, initialEdgeConnectionReportState, setPropInit(...OPS));
      },
    }),
  ),
);
